import React, { useState, useContext, useEffect, useMemo } from 'react'
import TableRangeKilometers from 'src/components/utils/tableRangeKilometers'
import { M_Button, M_InputTextField, M_DrawerTableTextField, M_Select, BufferService, LoadingContext, MMap } from 'src/components/M_Components'
import { DeleteOutline } from '@mui/icons-material'
import TableCostsCustomer from 'src/components/utils/tableCostsCustomer'
import { useTranslation } from 'react-i18next'
import * as turf from '@turf/turf'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import 'mapbox-gl/dist/mapbox-gl.css'
import './quotes.css'

export default function LocationSet({ dataQuotes, setDataQuotes, rowCost, setRowCost, operation, tablesRange, setTablesRange, optionsZone, setOptionsZone, optionsRange, setOptionsRange, firstRowAdded, setFirstRowAdded, mapView, setMapView, zoomMap, setZoomMap, centerMap, drawnFeatures, setDrawnFeatures, drawPolygon, setDrawPolygon, count, setCount }) {
    const [circlesData, setCirclesData] = useState({ location: null, radius: null })
    const ctx = useContext(LoadingContext)
    const bufferServicePriceTableDetail = new BufferService('PriceTableDetail')
    const { t } = useTranslation()
    const formRangeKilometers = [{ Name: t("Name"), type: 'text' }]

    const normalizeCoordinateValue = (value, maxAbs, referenceValue = null) => {
        const parsed = Number(value)
        if (!Number.isFinite(parsed)) return parsed

        if (Math.abs(parsed) <= maxAbs) return parsed

        let bestValue = parsed
        let bestScore = Number.POSITIVE_INFINITY

        for (let power = 1; power <= 18; power++) {
            const candidate = parsed / Math.pow(10, power)
            if (Math.abs(candidate) > maxAbs) continue

            const score = referenceValue == null
                ? Math.abs(candidate)
                : Math.abs(candidate - referenceValue)

            if (score < bestScore) {
                bestScore = score
                bestValue = candidate
            }
        }

        return bestValue
    }

    const normalizeLocationRing = (location) => {
        if (!Array.isArray(location)) return location
        return location.map((point) => {
            if (!Array.isArray(point) || point.length < 2) return point
            const lng = normalizeCoordinateValue(point[0], 180, centerMap?.[0])
            const lat = normalizeCoordinateValue(point[1], 90, centerMap?.[1])
            return [lng, lat]
        })
    }

    useEffect(() => {
        bufferServicePriceTableDetail.options().then((response) => {
            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', response.response.data.Errors[0], '')
                return
            }
            const filteredOptions = response.filter((option) => !option.IsHiddenList)
            setOptionsRange(filteredOptions)
        })

        if (operation === 'edit') {
            const radius = dataQuotes.get("Radius")
            const rawLocation = dataQuotes.get("Location")
            const normalizedLocation = normalizeLocationRing(rawLocation)
            const center = normalizedLocation?.[0]

            if (Array.isArray(normalizedLocation) && normalizedLocation.length > 0) {
                dataQuotes.set("Location", normalizedLocation)
            }

            if (radius && center) {
                setCirclesData({ location: center, radius: Number(radius) })
            }
            const priceTableList = dataQuotes.get("PriceTableList")
            priceTableList.forEach((elem) => {
                elem.PriceTableDetailList.forEach((element, i) => {
                    element.Id = element.Id
                    element.id = i + 1
                })
            })
            dataQuotes.set("PriceTableList", priceTableList)
            setTablesRange(dataQuotes.get("PriceTableList"))

            setDrawPolygon(normalizedLocation)
            setDataQuotes(new Map(dataQuotes))
            if (priceTableList && priceTableList.length > 0) {
                const newOptions = [...optionsZone]
                priceTableList.forEach(item => {
                    item.Name = item.Name
                    newOptions.push({ id: item.Id, label: item.Name, value: item.Name })
                })
                setOptionsZone(newOptions)
                setTablesRange([])
                const createdTablesEdit = []
                for (let i = 0; i < dataQuotes.get('PriceTableList').length; i++) { //está a criar as tabelas que vem do priceTableList
                    const newTable = {
                        id: dataQuotes.get('PriceTableList')[i].Id,
                        Name: dataQuotes.get('PriceTableList')[i].Name,
                        PricePlanId: dataQuotes.get('PriceTableList')[i].PricePlanId,
                        PriceTableDetailList: priceTableList[i].PriceTableDetailList,
                    }
                    createdTablesEdit.push(newTable)
                }
                setTablesRange(createdTablesEdit)
                dataQuotes.set('PriceTableList', priceTableList)
                setDataQuotes(new Map(dataQuotes))
            }
        } else if (operation === 'create') {
            if (tablesRange.length === 0) {
                addDefaultTable()
                addLineZone()
            }
        }
    }, [])

    const addDefaultTable = () => {
        if (tablesRange.length === 0 || (tablesRange[0].Name && tablesRange[0].Name.trim() !== '')) {
            const newRow = {
                id: 1,
                Limit: 1,
            }
            const defaultColumnValues = {}
            optionsRange.forEach((option) => {
                defaultColumnValues[option.Header] = 0
            })
            const newTableName = ''
            const newTable = {
                id: tablesRange.length === 0 ? 1 : tablesRange[tablesRange.length - 1].id + 1,
                Name: newTableName,
                PriceTableDetailList: [newRow],
            }
            const newArray = [...tablesRange, newTable]
            setTablesRange(newArray)
            dataQuotes.set('PriceTableList', newArray)
            setDataQuotes(new Map(dataQuotes))
            setFirstRowAdded(true)
        } else {
            ctx.toastMessages('error', '', t("FillTableName"))
        }
    }

    const columnsRange = optionsRange.map((option) => ({
        field: option.Name,
        header: option.NormalizedName,
    }))

    const addLocationSet = (event, tableIndex) => {
        const optionName = event.target.value
        const tempTable = { ...tablesRange[tableIndex], Name: optionName }
        let tempOptions = []

        if (optionsZone.some((option) => option.id === tempTable.id)) {
            tempOptions = optionsZone.map((value) => {
                if (value.id === tempTable.id) {
                    return { ...value, label: optionName, value: optionName }
                }
                return value
            })
        } else {
            tempOptions = [
                ...optionsZone,
                { id: tempTable.id, label: tempTable.Name, value: tempTable.Name },
            ]
        }
        setOptionsZone(tempOptions)
        const updatedTables = tablesRange.map((value) => {
            if (value.id === tempTable.id) {
                return tempTable
            }
            return value
        })
        setTablesRange(updatedTables)
        dataQuotes.set('PriceTableList', updatedTables)

        setDataQuotes(new Map(dataQuotes))
    }

    const removeTable = (tableIndex) => {
        let updatedTables = [...tablesRange]

        const tableName = updatedTables.find(obj => obj.id === tableIndex)?.Name

        updatedTables = updatedTables.filter(obj => obj.id !== tableIndex)
        setTablesRange(updatedTables)
        dataQuotes.set('PriceTableList', updatedTables)

        const updatedOptions = optionsZone.filter(option => option.label !== tableName)
        setOptionsZone(updatedOptions)
        setDataQuotes(new Map(dataQuotes))
    }

    const RowChangeValue = (index, newRowRange) => {
        const updatedTables = [...tablesRange]
        const updatedTable = { ...updatedTables[index], PriceTableDetailList: newRowRange }
        updatedTables[index] = updatedTable
        setTablesRange(updatedTables)
    }

    const mapFeatures = useMemo(() => {
        const features = []
        const locationCoordinates = dataQuotes.get("Location")
        const normalizedDrawPolygon =
            Array.isArray(drawPolygon) &&
                Array.isArray(drawPolygon[0]) &&
                Array.isArray(drawPolygon[0][0]) &&
                Array.isArray(drawPolygon[0][0][0])
                ? drawPolygon[0]
                : drawPolygon
        const hasValidRing =
            Array.isArray(locationCoordinates) &&
            locationCoordinates.length > 2 &&
            Array.isArray(locationCoordinates[0]) &&
            locationCoordinates[0].length === 2

        const radius = Number(dataQuotes.get("Radius"))
        const center = dataQuotes.get("Location")?.[0]
        const hasCircle = operation === 'edit' && radius > 0 && Array.isArray(center) && center.length === 2

        if (!hasCircle && operation === 'edit' && Array.isArray(normalizedDrawPolygon) && normalizedDrawPolygon.length > 0) {
            features.push({
                id: 'location-edit-polygon',
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'Polygon',
                    coordinates: [normalizedDrawPolygon],
                },
            })
        }
        else if (!hasCircle && hasValidRing && (!Array.isArray(drawnFeatures) || drawnFeatures.length === 0)) {
            features.push({
                id: 'location-current-polygon',
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'Polygon',
                    coordinates: [locationCoordinates],
                },
            })
        }

        if (hasCircle) {
            const circle = turf.circle(center, radius, { steps: 50, units: 'kilometers' })
            features.push({
                id: 'location-edit-circle',
                type: 'Feature',
                properties: { isCircle: true },
                geometry: {
                    type: 'Polygon',
                    coordinates: circle.geometry.coordinates,
                },
            })
        }

        if (Array.isArray(drawnFeatures) && drawnFeatures.length > 0) {
            features.push(...drawnFeatures.filter((feature) => feature?.geometry?.type === "Polygon"))
        }

        return features
    }, [operation, drawPolygon, drawnFeatures, dataQuotes])

    const handleMapDrawCreate = (e) => {
        if (e?.rejected) {
            ctx.toastMessages('error', '', t("ErrorCreatePolygon"))
            return
        }

        const createdFeature = e?.features?.[0]
        if (!createdFeature || createdFeature.geometry?.type !== "Polygon") return

        if (createdFeature.properties?.isCircle) {
            const circleCenter = circlesData?.location
                || (Array.isArray(createdFeature.geometry?.coordinates?.[0]) && createdFeature.geometry.coordinates[0][0]
                    ? createdFeature.geometry.coordinates[0][0]
                    : null)
            const circleRadius = Number(circlesData?.radius)

            if (circleCenter) {
                dataQuotes.set("Location", [circleCenter])
            }
            if (Number.isFinite(circleRadius) && circleRadius > 0) {
                dataQuotes.set("Radius", circleRadius)
            }
            setDrawnFeatures([])
            setDrawPolygon([])
            setDataQuotes(new Map(dataQuotes))
            ctx.toastMessages('success', '', t("DrawCircle"))
            return
        }

        dataQuotes.set('Location', createdFeature.geometry.coordinates[0])
        dataQuotes.delete('Radius')
        setCirclesData({ location: null, radius: null })
        setDataQuotes(new Map(dataQuotes))
        setDrawnFeatures([createdFeature])
        ctx.toastMessages('success', '', t("SuccessCreatePolygon"))
    }

    const handleMapDrawUpdate = ({ features }) => {
        if (!features?.length) return

        const updatedFeature = features[0]
        if (updatedFeature.geometry?.type !== "Polygon") return

        if (!updatedFeature.properties?.isCircle) {
            dataQuotes.set('Location', updatedFeature.geometry.coordinates[0])
            dataQuotes.delete('Radius')
            setDataQuotes(new Map(dataQuotes))
            setDrawnFeatures((prevFeatures) => {
                const hasFeature = prevFeatures.some((feature) => feature.id === updatedFeature.id)
                if (!hasFeature) return [updatedFeature]
                return prevFeatures.map((feature) => feature.id === updatedFeature.id ? updatedFeature : feature)
            })
        }
    }

    const handleMapDrawDelete = ({ features }) => {
        if (!features?.length) return

        const deletedFeature = features[0]
        setDrawnFeatures((prevFeatures) =>
            prevFeatures.filter((feature) => feature.id !== deletedFeature.id)
        )

        if (deletedFeature.properties?.isCircle) {
            dataQuotes.delete('Radius')
            dataQuotes.delete('Location')
        } else {
            const hasActiveCircle = Number(dataQuotes.get('Radius')) > 0
            if (hasActiveCircle) {
                setDataQuotes(new Map(dataQuotes))
                ctx.toastMessages('success', '', t("DeletedSuccess"))
                return
            }
            dataQuotes.delete('Location')
            setDrawPolygon([])
        }
        setDataQuotes(new Map(dataQuotes))
        ctx.toastMessages('success', '', t("DeletedSuccess"))
    }

    const addLineZone = () => {
        const newLine = {
            Id: count + 1,
            FromPriceZone: '',
            ToPriceZone: '',
            PriceTable: '',
        }

        setCount(newLine.Id)
        const getZones = dataQuotes.get("PricePlanDetailList") || []
        const newArray = [...getZones, newLine]
        dataQuotes.set("PricePlanDetailList", newArray)
        setDataQuotes(new Map(dataQuotes))
    }

    const handleZoneChange = (e, Id, field, isDrawer) => {
        const getZones = dataQuotes.get("PricePlanDetailList") || []
        const updatedLine = [...getZones]
        const targetIndex = updatedLine.findIndex(line => line.Id === Id)
        if (targetIndex !== -1) {
            const updatedTargetLine = { ...updatedLine[targetIndex] }
            updatedTargetLine[field] = isDrawer ? e : e.target.value
            updatedLine[targetIndex] = updatedTargetLine
            dataQuotes.set("PricePlanDetailList", updatedLine)
            setDataQuotes(new Map(dataQuotes))
        }
    }

    const removeLineZone = (Id) => {
        const listPricePlanDetail = dataQuotes.get("PricePlanDetailList")
        if (listPricePlanDetail.length <= 1) {
            ctx.toastMessages('error', '', t("ErrorDeletingLine"))
        } else {
            const updateZones = dataQuotes.get("PricePlanDetailList").filter(zone => zone.Id !== Id)
            dataQuotes.set("PricePlanDetailList", updateZones)
            setDataQuotes(new Map(dataQuotes))
        }
    }

    return (
        <>
            <div className='divLocationPostalCodeGlobal'>
                <div className='cardPriceTable'>
                    <div className='divLabelPriceTable'>
                        <div className='divInteriorLabelPriceTable'>
                            <img src="/icons/svgsDaNet/logo-euro.svg"
                                style={{ width: '35px', marginRight: '10px', filter: 'invert(16%) sepia(66%) saturate(5073%) hue-rotate(347deg) brightness(74%) contrast(116%)' }}
                                alt="Price Table" />
                            {t("PriceTable")}
                        </div>
                        <div className='divInteriorButtonAddTable'>
                            <M_Button
                                onClick={addDefaultTable}
                                label={t("AddTablePrice")}
                                icon={<img src="/icons/svgsDaNet/file-plus.svg"
                                    style={{ filter: 'invert(8%) sepia(97%) saturate(6380%) hue-rotate(353deg) brightness(95%) contrast(105%)', width: '24px' }}
                                    alt="save" />
                                }
                                borderColor={'var(--color-grey)'}
                            />
                        </div>
                    </div>
                    <div className='divHeaderPriceTable'>
                        {tablesRange.map((table, index) => (
                            <div key={index}>
                                <div className='divInteriorHeaderPriceTable'>
                                    <div className='divNamePriceTable'>
                                        {formRangeKilometers.map((value, key) => (
                                            <div key={key} className='inputNamePriceTable'>
                                                <M_InputTextField
                                                    required={true}
                                                    label={value.Name}
                                                    type={'text'}
                                                    maxNumbers={50}
                                                    onChange={(e) => addLocationSet(e, index)}
                                                    value={dataQuotes.get("PriceTableList") ? dataQuotes?.get("PriceTableList")[index]?.Name : '' || ''}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className='divButtonsPriceTable'>
                                        {tablesRange.length > 1 && (
                                            <M_Button
                                                label={t("RemoveTable")}
                                                icon={<img src="/icons/svgsDaNet/trash-2.svg"
                                                    style={{ filter: 'invert(11%) sepia(68%) saturate(6715%) hue-rotate(359deg) brightness(93%) contrast(115%)', width: '24px' }}
                                                    alt="save" />
                                                }
                                                borderColor={'var(--color-grey)'}
                                                onClick={() => removeTable(table.id)}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className='divTablePriceTable'>
                                    <TableRangeKilometers
                                        key={index}
                                        id={table.id}
                                        PriceTableDetailList={table.PriceTableDetailList}
                                        setRowRange={(newRowRange) => RowChangeValue(index, newRowRange)}
                                        columnsRange={columnsRange}
                                        dataQuotes={dataQuotes}
                                        setDataQuotes={setDataQuotes}
                                        operation={operation}
                                        firstRowAdded={firstRowAdded}
                                        optionsRange={optionsRange}
                                    />
                                    {index !== tablesRange.length - 1 && <div className='dividerTablesPriceTable'></div>}
                                </div>
                            </div>

                        ))}
                    </div>
                </div>
                <div className='cardMap'>
                    <MMap
                        initalMapState={{
                            longitude: centerMap?.[0],
                            latitude: centerMap?.[1],
                            zoom: zoomMap,
                        }}
                        initialMapView={mapView === 'satellite-v9' ? 'satellite-streets-v11' : mapView}
                        onMapViewChange={(newMapView) => {
                            setMapView(newMapView === 'satellite-streets-v11' ? 'satellite-v9' : newMapView)
                        }}
                        onZoomChange={(newZoom) => {
                            if (newZoom != null) setZoomMap(newZoom)
                        }}
                        height='500px'
                        polygonCreation
                        maxPolygons={1}
                        circlesNumber={1}
                        exclusiveCircleOrPolygon
                        circlesData={circlesData}
                        setCirclesData={setCirclesData}
                        onDrawCreate={handleMapDrawCreate}
                        onDrawUpdate={handleMapDrawUpdate}
                        onDrawDelete={handleMapDrawDelete}
                        polygonsData={mapFeatures}
                        satelliteView
                        navigationControl
                        controlScale
                    />
                </div>
            </div>

            <div className='displayCardsZoneCosts'>
                <div className='divCardZone'>
                    <div className='divLabelZoneAdditionals'>
                        <img src="/icons/svgsDaNet/globe.svg"
                            style={{ width: '30px', marginRight: '10px', filter: 'invert(16%) sepia(66%) saturate(5073%) hue-rotate(347deg) brightness(74%) contrast(116%)' }}
                            alt="Zone" />
                        {t("Zones")}
                    </div>
                    <div className='divButtonAddZoneCost'>
                        <M_Button
                            label={t("AddZone")}
                            icon={<img src="/icons/svgsDaNet/add-circle.svg"
                                style={{ filter: 'invert(42%) sepia(44%) saturate(4434%) hue-rotate(74deg) brightness(97%) contrast(101%)', width: '24px' }}
                                alt="save" />
                            }
                            borderColor={'var(--color-grey)'}
                            onClick={addLineZone}
                        />
                    </div>
                    <div className='divIconsZone'>
                        <div className='divInteriorIconsZone'>
                            <img src="/icons/svgsDaNet/map-pin.svg"
                                style={{ width: '20px' }}
                                alt="From Zone" />
                            {t("FromZone")}
                        </div>
                        <div className='divInteriorIconsZone'>
                            <img src="/icons/svgsDaNet/flag.svg"
                                style={{ width: '20px' }}
                                alt="To Zone" />
                            {t("ToZone")}
                        </div>
                        <div className='divInteriorIconsZone'>
                            <img src="/icons/svgsDaNet/logo-euro.svg"
                                style={{ width: '25px' }}
                                alt="Price Table" />
                            {t("PriceTable")}
                        </div>
                    </div>

                    <div className='divLinesZone'>
                        {dataQuotes.get("PricePlanDetailList")?.map((line, index) => (
                            <div key={line?.Id ?? index} className='divGlobalSelectZones'>
                                <div className='divSelectZones' >
                                    <div>
                                        <M_DrawerTableTextField
                                            key={index}
                                            required={true}
                                            entity={"Pricezone"}
                                            value={line?.FromPriceZoneId ?
                                                {
                                                    PriceZoneId: line?.FromPriceZoneId,
                                                    ZoneCode: line?.ZoneCode,
                                                    Name: line.FromPriceZone,
                                                } : null}
                                            filterField={'Name'}
                                            labelFields={['Name']}
                                            onSubmitValue={obj => {
                                                if (obj) {
                                                    handleZoneChange(obj.PriceZoneId, line.Id, 'FromPriceZoneId', true)
                                                    handleZoneChange(obj.ZoneCode, line.Id, 'ZoneCode', true)
                                                    handleZoneChange(obj.Name, line.Id, 'FromPriceZone', true)
                                                } else {
                                                    handleZoneChange(null, line.Id, 'FromPriceZoneId', true)
                                                    handleZoneChange(null, line.Id, 'ZoneCode', true)
                                                    handleZoneChange(null, line.Id, 'FromPriceZone', true)
                                                }
                                            }}
                                            valueField={'FromPriceZoneId'}
                                        />
                                    </div>
                                    <div>
                                        <M_DrawerTableTextField
                                            key={index}
                                            required={true}
                                            entity={"Pricezone"}
                                            value={line?.ToPriceZoneId ?
                                                {
                                                    PriceZoneId: line?.ToPriceZoneId,
                                                    ZoneCode: line?.ZoneCode,
                                                    Name: line.ToPriceZone,
                                                } : null}
                                            filterField={'Name'}
                                            labelFields={['Name']}
                                            onSubmitValue={obj => {
                                                if (obj) {
                                                    handleZoneChange(obj.PriceZoneId, line.Id, 'ToPriceZoneId', true)
                                                    handleZoneChange(obj.ZoneCode, line.Id, 'ZoneCode', true)
                                                    handleZoneChange(obj.Name, line.Id, 'ToPriceZone', true)
                                                } else {
                                                    handleZoneChange(null, line.Id, 'ToPriceZoneId', true)
                                                    handleZoneChange(null, line.Id, 'ZoneCode', true)
                                                    handleZoneChange(null, line.Id, 'ToPriceZone', true)
                                                }
                                            }}
                                            valueField={'ToPriceZoneId'}
                                        />
                                    </div>
                                    <div>
                                        <M_Select
                                            key={index}
                                            value={line.PriceTable || ""}
                                            options={optionsZone}
                                            onChange={(e) => handleZoneChange(e, line.Id, 'PriceTable')}
                                        />
                                    </div>
                                </div>
                                <div className='divDeleteLineZone'>
                                    <DeleteOutline
                                        style={{ cursor: 'pointer', color: 'var(--color-red)' }}
                                        onClick={() => removeLineZone(line.Id)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className='divCardAdditionals'>
                    <div className='divLabelZoneAdditionals'>
                        <img src="/icons/svgsDaNet/calculator.svg"
                            style={{ width: '30px', marginRight: '10px', filter: 'invert(16%) sepia(66%) saturate(5073%) hue-rotate(347deg) brightness(74%) contrast(116%)' }}
                            alt="Additionals" />
                        {t("Additionals")}
                    </div>
                    <div className='divTableCosts'>
                        <TableCostsCustomer rowCost={rowCost} setRowCost={setRowCost} />
                    </div>
                </div>
            </div >
            <br /><br />
        </>
    )
}